import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import Button from './Buttons';
import SplitButton from './SplitButton';
import { CustomSelect } from './CustomFormElements';
import ProgressComponent from './ProgressComponent';
import TableSearchInput from './TableSearchInput';
import './Tables.css';
import { RowType, TablePProps } from './TablesTypes';
import {
  getTablePaginationFromStorage,
  setTablePageSizeStorage,
  setTablePageStorage,
} from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

// ✅ Default Expandable Row Component
const DefaultExpandableRow = ({ data }) => (
  <div className="table-folded-content">
    <div dangerouslySetInnerHTML={{ __html: data.description || '' }} />
  </div>
);

// ✅ Main Table Component
function TableP<T extends RowType>({
  title,
  columns,
  data: propData = [],
  selectableRows = false,
  selectableRowsHighlight = false,
  onSelectedRowsChange,
  clearSelectedRows: externalClearSelectedRows = false,
  expandableRows = false,
  expandableRowsComponent = DefaultExpandableRow,
  expandableRowsComponentProps = {},
  paginationPerPage = 10,
  paginationTotalRows,
  pagination = true,
  resetPaginationToggle = false,
  dense = false,
  noTableHead = false,
  disabled = false,
  progressPending = false,
  progressComponent = <ProgressComponent />,
  onRowClicked = () => {},
  onRowDoubleClicked = () => {},
  onRowMouseEnter = () => {},
  onRowMouseLeave = () => {},
  onChangePage = () => {},
  sortIcon,
  onDelete,
  singleSelect = true,
  getRowUrl,
  rowUrlField = 'url',
  onChangeFilter = () => {},
  defaultValue = null,
  typeSearch = null,
  searchOptions = [],
  headerActions = null,
  contextActionsExtra = null,
  deleteAllByFilter = null,
}: TablePProps<T>) {
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [toggleClearSelectedRow, setToggleClearSelectedRow] = useState<boolean>(false);
  const [changePageEvents, setChangePageEvents] = useState<{ skip: number; limit: number }[]>([]);
  const storagePrefix = formatTableTitle(String(title));

  // Wrapper ref to capture middle clicks
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const { persistedPageSize, persistedPage } = getTablePaginationFromStorage(
    storagePrefix,
    paginationPerPage,
  );

  const [currentPage, setCurrentPage] = useState(persistedPage);
  const [pageSize, setPageSize] = useState(persistedPageSize);

  // Allow the parent to force-clear the selection via a toggling prop.
  const isFirstExternalClear = useRef(true);
  useEffect(() => {
    if (isFirstExternalClear.current) {
      isFirstExternalClear.current = false;
      return;
    }
    setSelectedRows([]);
    setToggleClearSelectedRow((prev) => !prev);
  }, [externalClearSelectedRows]);

  const customStyles = useMemo(
    () => ({
      table: {
        style: {
          backgroundColor: 'transparent',
          color: 'var(--bs-body-text-color)',
        },
      },
      tableWrapper: { style: { backgroundColor: 'transparent' } },
      responsiveWrapper: { style: { backgroundColor: 'transparent' } },
      progress: {
        style: {
          backgroundColor: 'transparent',
          color: 'var(--bs-body-text-color)',
        },
      },
      noData: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
          padding: '24px',
        },
      },
      header: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
        },
      },
      subHeader: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
        },
      },
      head: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
        },
      },
      headRow: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
          borderBottomColor: 'var(--bs-table-border)',
        },
      },
      headCells: {
        style: {
          color: 'var(--bs-body-text-color)',
        },
      },
      rows: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
          borderBottomColor: 'var(--bs-table-border)',
        },
        stripedStyle: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
        },
      },
      cells: {
        style: {
          color: 'var(--bs-body-text-color)',
        },
      },
      expanderRow: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
        },
      },
      expanderCell: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
        },
      },
      expanderButton: {
        style: {
          color: 'var(--bs-body-text-color)',
          fill: 'var(--bs-body-text-color)',
          backgroundColor: 'transparent',
          '&:hover:not(:disabled)': {
            backgroundColor: 'var(--bs-table-row-hover)',
          },
          '&:focus': {
            backgroundColor: 'var(--bs-table-row-hover)',
          },
          svg: {
            fill: 'var(--bs-body-text-color)',
          },
        },
      },
      pagination: {
        style: {
          backgroundColor: 'var(--bs-box-bg)',
          color: 'var(--bs-body-text-color)',
          borderTopColor: 'var(--bs-table-border)',
        },
        pageButtonsStyle: {
          color: 'var(--bs-body-text-color)',
          fill: 'var(--bs-body-text-color)',
          '&:disabled': {
            color: 'var(--bs-secondary)',
            fill: 'var(--bs-secondary)',
          },
          '&:hover:not(:disabled)': {
            backgroundColor: 'var(--bs-table-row-hover)',
          },
        },
      },
    }),
    [],
  );

  const handleChangePage = (skip: number, limit: number) => {
    setChangePageEvents((prevEvents) => [...prevEvents, { skip, limit }]);
    setTimeout(() => {
      setChangePageEvents((pe) => {
        if (pe.length > 0) {
          const e = pe[pe.length - 1];
          console.log('Last event:', e);
          setTimeout(() => {
            onChangePage(e.skip, e.limit);
          }, 10);
        }
        return [];
      });
    }, 10);
  };

  // Handle Row Selection
  const handleRowSelected = ({ selectedRows }: { selectedRows: T[] }) => {
    setSelectedRows(selectedRows);

    // Optional manual highlight (DataTable can already highlight if selectableRowsHighlight=true)
    const allRows = document.querySelectorAll('.rdt_TableRow');
    allRows.forEach((row) => {
      const rowId = row.getAttribute('id');
      const isSelected = selectedRows.some((selectedRow) => `row-${selectedRow.id}` === rowId);
      if (isSelected) row.classList.add('selected');
      else row.classList.remove('selected');
    });

    if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows });
  };

  const handleDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    const idsToDelete = selectedRows.map((row) => row.id || row.key);
    if (onDelete) {
      onDelete(idsToDelete);
      setToggleClearSelectedRow(!toggleClearSelectedRow);
    }
  }, [selectedRows, onDelete, toggleClearSelectedRow]);

  const contextActions = deleteAllByFilter ? (
    <>
      <SplitButton
        options={[
          { label: 'Delete Selected', onClick: handleDelete },
          { label: deleteAllByFilter.label, onClick: deleteAllByFilter.onClick },
        ]}
        disabled={selectedRows.length === 0}
        variant="danger"
        size="medium"
      />
      {contextActionsExtra}
    </>
  ) : (
    <>
      <Button
        onClick={handleDelete}
        variant="danger"
        size="medium"
        disabled={selectedRows.length === 0}
      >
        Delete Selected
      </Button>
      {contextActionsExtra}
    </>
  );

  // ✅ Automatically Detect Expandable Rows (fallback)
  const derivedExpandableRowsComponent = useMemo(() => {
    return ({ data }) =>
      data.description ? (
        <div className="table-folded-content">
          <div dangerouslySetInnerHTML={{ __html: data.description }} />
        </div>
      ) : null;
  }, []);

  // ---------------------------- Search input for the table header ------------------
  // --- SEARCH state (server-side) ---
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>([]);

  useEffect(() => {
    if (defaultValue) {
      setMultiSelectValues(defaultValue);
    }
  }, [defaultValue]);

  const resetPage = () => {
    setCurrentPage(1);
    setTablePageStorage(storagePrefix, 1);
  };

  const SearchComponent =
    typeSearch === 'default' ? (
      <TableSearchInput
        onSearch={(value) => {
          resetPage();
          if (onChangeFilter) {
            onChangeFilter(0, pageSize, value);
          }
        }}
      />
    ) : typeSearch === 'multiselect' && searchOptions && searchOptions.length > 0 ? (
      <div
        className="input-block d-flex flex-row justify-between"
        style={{ flex: '0 0 auto', width: 400 }}
      >
        <CustomSelect
          id="multi-search-select"
          label="Search"
          options={searchOptions.map((item) => ({ value: item.value, label: item.label }))}
          value={multiSelectValues}
          multiSelect={true}
          onChange={(val) => {
            const nextValue = Array.isArray(val) ? val : [];
            setMultiSelectValues(nextValue);
            resetPage();
            if (onChangeFilter) {
              onChangeFilter(0, pageSize, nextValue);
            }
          }}
          placeholder={'Select types'}
        />
      </div>
    ) : null;
  // ---------------------------- END OF Search input for the table header ------------------

  // ---------- NEW: header sub-component (search + actions) ----------
  const SubHeader = (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      {typeSearch ? <div>{SearchComponent}</div> : null}
      {headerActions ? <div className="d-flex align-items-center">{headerActions}</div> : null}
    </div>
  );

  // ---------- NEW: URL resolution helpers ----------
  const resolveRowUrl = useCallback(
    (row: T) => {
      if (typeof getRowUrl === 'function') return getRowUrl(row);
      if (row && row[rowUrlField]) return row[rowUrlField];
      if (row && row.url) return row.url;
      if (row && row.link) return row.link;
      if (row && row.href) return row.href;
      return null;
    },
    [getRowUrl, rowUrlField],
  );

  const openRowInNewTab = useCallback(
    (row: T) => {
      const url = resolveRowUrl(row);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },
    [resolveRowUrl],
  );

  // ---------- NEW: Middle-click (and Safari fallback) on rows ----------
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return undefined;

    const handleAuxOrMouseDown = (e: MouseEvent) => {
      // React only fires onClick for primary button; we need native events.
      // Only act on middle button.
      if (e.button !== 1) return;

      // Let native <a href> inside cells handle their own middle-clicks
      if (e.target && (e.target as Element).closest && (e.target as Element).closest('a[href]'))
        return;

      const rowEl = (e.target as Element).closest?.('.rdt_TableRow');
      if (!rowEl) return;

      const body = wrapper.querySelector('.rdt_TableBody');
      if (!body) return;

      const rows = Array.from(body.querySelectorAll('.rdt_TableRow'));
      const idx = rows.indexOf(rowEl);
      if (idx < 0) return;

      const row = propData[idx];
      if (!row) return;

      openRowInNewTab(row);
      e.preventDefault();
    };

    // Modern browsers: 'auxclick' captures middle-clicks reliably
    wrapper.addEventListener('auxclick', handleAuxOrMouseDown);
    // Safari fallback: listen to mousedown
    wrapper.addEventListener('mousedown', handleAuxOrMouseDown);

    return () => {
      wrapper.removeEventListener('auxclick', handleAuxOrMouseDown);
      wrapper.removeEventListener('mousedown', handleAuxOrMouseDown);
    };
  }, [propData, openRowInNewTab]);

  // ---------- BONUS: Ctrl/Cmd + left click opens in new tab ----------
  const handleRowClick = useCallback(
    (row: T, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event && (event.metaKey || event.ctrlKey)) {
        openRowInNewTab(row);
        // don't swallow user's own handler
      }
      onRowClicked(row, event);
    },
    [openRowInNewTab, onRowClicked],
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage === 1) {
      return;
    }
    setCurrentPage(1);
    setTablePageStorage(storagePrefix, 1);
  }, [resetPaginationToggle, storagePrefix]);

  return (
    <div ref={tableWrapperRef}>
      <DataTable
        customStyles={customStyles}
        title={title}
        columns={columns}
        data={propData}
        selectableRows={selectableRows}
        selectableRowsSingle={singleSelect}
        subHeader
        subHeaderComponent={typeSearch || headerActions ? SubHeader : null}
        selectableRowsHighlight={selectableRowsHighlight}
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleClearSelectedRow}
        contextActions={contextActions}
        responsive
        striped
        highlightOnHover
        pagination={pagination}
        paginationServer
        paginationPerPage={pageSize}
        paginationDefaultPage={currentPage}
        paginationTotalRows={paginationTotalRows}
        paginationRowsPerPageOptions={[10, 25, 50, 75, 100, 250, 500]}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent || derivedExpandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        progressPending={progressPending}
        progressComponent={progressComponent}
        dense={dense}
        noTableHead={noTableHead}
        disabled={disabled}
        onRowClicked={handleRowClick}
        onRowDoubleClicked={onRowDoubleClicked}
        onRowMouseEnter={onRowMouseEnter}
        onRowMouseLeave={onRowMouseLeave}
        onChangePage={(page, totalRows) => {
          console.log('Page changed:', page, totalRows, paginationPerPage);
          setTablePageStorage(storagePrefix, page);

          setCurrentPage(page);
          const skip = (page - 1) * pageSize;
          handleChangePage(skip, pageSize);
        }}
        onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
          console.log('Rows per page changed:', currentRowsPerPage, currentPage);
          setTablePageSizeStorage(storagePrefix, currentRowsPerPage);
          setTablePageStorage(storagePrefix, 1);

          setPageSize(currentRowsPerPage);
          setCurrentPage(1);
          const skip = 0;
          handleChangePage(skip, currentRowsPerPage);
        }}
        sortIcon={sortIcon}
        pointerOnHover
      />
    </div>
  );
}

export default TableP;
